// src/pages/DepartamentosIdentificacion.js
import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaIdCard, FaFileExcel, FaUserTie, FaChevronLeft, FaDownload } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { collection, getDocs, query, orderBy, doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import * as XLSX from 'xlsx';
import ConfirmDialog from '../components/ConfirmDialog';
import './DepartamentosDetalle.css';

const DepartamentosIdentificacion = () => {
    const { fraccionamientoId } = useAuth();
    const propietariosUploadRef = useRef(null);
    const [stats, setStats] = useState({
        propietarios: 0,
        arrendados: 0,
        sinContacto: 0,
    });
    const [loading, setLoading] = useState(true);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successSummary, setSuccessSummary] = useState('');
    const [uploadMessage, setUploadMessage] = useState(''); // Toast para feedback visual

    useEffect(() => {
        if (!fraccionamientoId) return;

        const fetchStats = async () => {
            try {
                setLoading(true);
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const q = query(casasRef);
                const snap = await getDocs(q);

                let propietariosCount = 0;
                let arrendadosCount = 0;
                let sinContactoCount = 0;

                snap.forEach((doc) => {
                    const casa = doc.data();
                    const perfil = String(casa.perfil || '').trim().toLowerCase();
                    const email = String(casa.correo || casa.email || '').trim();
                    const telefono = String(casa.telefono || casa.telefonoPrincipal || '').trim();

                    // Excluir caseta/guardia/security
                    if (perfil === 'caseta' || perfil === 'guardia' || perfil === 'security') {
                        return;
                    }

                    // Contar propietarios
                    if (perfil === 'propietario' || perfil === 'propietario/residente' || perfil === 'dueño' || perfil === 'owner') {
                        propietariosCount++;
                        
                        // Propietarios sin contacto
                        if (!email && !telefono) {
                            sinContactoCount++;
                        }
                    }

                    // Contar arrendados
                    if (['arrendador', 'arrendatario', 'arrendataria', 'arrendada', 'arrendado', 'inquilino', 'renter', 'rentado'].includes(perfil)) {
                        arrendadosCount++;
                    }
                });

                setStats({
                    propietarios: propietariosCount,
                    arrendados: arrendadosCount,
                    sinContacto: sinContactoCount,
                });
            } catch (error) {
                console.error('Error cargando estadísticas:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [fraccionamientoId]);

    const triggerUpload = () => {
        if (propietariosUploadRef.current) {
            propietariosUploadRef.current.value = '';
            propietariosUploadRef.current.click();
        }
    };

    // Subir Excel y guardar propietarios en colección separada: contacts
    const handleExcelUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !fraccionamientoId) return;
        try {
            // Leer y procesar el archivo Excel
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            // Subir a Storage para respaldo
            const storagePath = `imports_contacts/${fraccionamientoId}/${Date.now()}-${file.name}`;
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, file);

            // Guardar propietarios en colección dedicada (no toca 'casas')
            const ownersCol = collection(db, 'fraccionamientos', fraccionamientoId, 'contacts');
            let updatedCount = 0;
            const updatedNumbers = [];
            for (const row of jsonData) {
                const numero = String(row.numero_vivienda || row.numero || row.casa || '').trim();
                if (!numero) continue;

                const ownerContact = {
                    numero_vivienda: numero,
                    nombre_propietario: String(row.nombre_propietario || row.nombre || '').trim(),
                    telefono: String(row.telefono || row.telefono1 || '').trim(),
                    telefono2: String(row.telefono2 || '').trim(),
                    email: String(row.email || row.correo || '').trim(),
                    localidad: String(row.localidad || row.estado || row.ubicacion || '').trim(),
                    ultimaActualizacion: new Date().toISOString(),
                };

                // Usamos 'numero_vivienda' como ID de documento para fácil join
                const ownerDocRef = doc(ownersCol, numero);
                await setDoc(ownerDocRef, ownerContact, { merge: true });
                updatedCount++;
                updatedNumbers.push(numero);
            }

            const sample = updatedNumbers.slice(0, 10).join(', ');
            setSuccessSummary(`✓ Archivo procesado correctamente. Filas detectadas: ${jsonData.length}. Viviendas actualizadas: ${updatedCount}. ${updatedCount > 0 ? 'Ejemplos: ' + sample : ''}`);
            setUploadMessage(`✓ ${updatedCount} viviendas actualizadas correctamente`);
            setShowSuccessDialog(true);
            console.log(`✓ ${updatedCount} viviendas actualizadas con datos de propietarios`);
            
            // Limpiar el mensaje después de 5 segundos
            setTimeout(() => setUploadMessage(''), 5000);
        } catch (err) {
            console.error('Error subiendo archivo de propietarios:', err);
            const errorMsg = 'No se pudo procesar el archivo. Verifica que tenga las columnas: numero_vivienda, nombre_propietario, telefono, telefono2, email, localidad.';
            setErrorMessage(errorMsg);
            setUploadMessage(`✗ Error: ${err.message || 'No se pudo procesar el archivo'}`);
            setShowErrorDialog(true);
            
            // Limpiar el mensaje después de 6 segundos
            setTimeout(() => setUploadMessage(''), 6000);
        }
    };

    // Descargar Excel con datos actuales de propietarios (desde contacts) y arrendatarios (desde casas)
    const handleDownloadExcel = async () => {
        if (!fraccionamientoId) return;
        try {
            const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
            const q = query(casasRef, orderBy('numero'));
            const casasSnap = await getDocs(q);

            const ownersRef = collection(db, 'fraccionamientos', fraccionamientoId, 'contacts');
            const ownersSnap = await getDocs(ownersRef);
            const ownersMap = new Map();
            ownersSnap.forEach(d => {
                const oc = d.data();
                const numero = String(oc.numero_vivienda || d.id || '').trim();
                if (numero) ownersMap.set(numero, oc);
            });

            const data = [];
            const rentalProfiles = new Set(['arrendador', 'arrendatario', 'arrendataria', 'arrendada', 'arrendado', 'inquilino', 'renter', 'rentado']);
            casasSnap.forEach((docSnap) => {
                const casa = docSnap.data();
                const perfil = String(casa.perfil || '').trim().toLowerCase();
                if (!rentalProfiles.has(perfil)) return;

                const numero = casa.numero || casa.numeroCasa || '';
                const owner = ownersMap.get(String(numero)) || {};
                data.push({
                    numero_vivienda: numero,
                    nombre_propietario: owner.nombre_propietario || '',
                    telefono: owner.telefono || '',
                    telefono2: owner.telefono2 || '',
                    email: owner.email || '',
                    localidad: owner.localidad || '',
                    arrendatario_actual: casa.nombre_residente || casa.nombre || '',
                    telefono_arrendatario: casa.telefono || '',
                    email_arrendatario: casa.correo || casa.email || '',
                });
            });

            if (data.length === 0) {
                setErrorMessage('No hay viviendas arrendadas con datos para descargar.');
                setShowErrorDialog(true);
                return;
            }

            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Propietarios');
            XLSX.writeFile(workbook, `propietarios_${fraccionamientoId}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (err) {
            console.error('Error descargando Excel:', err);
            setErrorMessage('No se pudo generar el archivo de descarga.');
            setShowErrorDialog(true);
        }
    };



    // Descargar plantilla con columnas esperadas
    const handleDownloadTemplate = () => {
        const headers = [
            { numero_vivienda: 'A-333', nombre_propietario: 'Casa arrendada A-333', telefono: '4271811975', telefono2: '', email: 'wongjonnatan1990@yahoo.com', localidad: 'Querétaro' },
        ];
        const ws = XLSX.utils.json_to_sheet(headers, { skipHeader: false });
        // Forzar cabeceras exactas
        ws.A1.v = 'numero_vivienda';
        ws.B1.v = 'nombre_propietario';
        ws.C1.v = 'telefono';
        ws.D1.v = 'telefono2';
        ws.E1.v = 'email';
        ws.F1.v = 'localidad';
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
        XLSX.writeFile(wb, 'plantilla_propietarios.xlsx');
    };

    return (
        <div className="departamentos-page">
            <div className="departamentos-container">
                <header className="departamentos-header">
                    <div className="departamentos-header-icon">
                        <FaIdCard />
                    </div>
                    <div>
                        <h1>Identificación de propietarios y arrendatarios</h1>
                        <p>Actualiza el padrón de dueños y controla qué viviendas se encuentran en arrendamiento activo.</p>
                    </div>
                    <Link to="/administracion" className="departamentos-back-link">
                        <FaChevronLeft /> Regresar a administración
                    </Link>
                </header>

                <section className="departamentos-content">
                    <article className="departamentos-card">
                        <div className="departamentos-card-header">
                            <h2 className="section-title"><FaUserTie /> Carga de propietarios</h2>
                            <div className="owner-nav-links">
                                <Link to="/admin/arrendados" className="nav-link">Departamentos arrendados</Link>
                                <Link to="/admin/propietarios" className="nav-link">Ver propietarios</Link>
                            </div>
                        </div>
                        <p className="help-text">Importa un archivo Excel con la información de cada propietario aunque no resida dentro del fraccionamiento.</p>
                        
                        <div className="owner-stats">
                            <div>
                                <span className="stat-label">Propietarios registrados</span>
                                <strong>{loading ? '...' : stats.propietarios}</strong>
                            </div>
                            <div>
                                <span className="stat-label">Viviendas arrendadas</span>
                                <strong>{loading ? '...' : stats.arrendados}</strong>
                            </div>
                            <div>
                                <span className="stat-label">Propietarios sin contacto</span>
                                <strong>{loading ? '...' : stats.sinContacto}</strong>
                            </div>
                        </div>

                        <div className="owner-grid">
                            <button type="button" className="primary-button" onClick={triggerUpload}>
                                <FaFileExcel /> Cargar padrón desde Excel
                            </button>
                            <button type="button" className="primary-button" onClick={handleDownloadExcel} style={{ backgroundColor: '#10b981' }}>
                                <FaDownload /> Descargar datos actuales
                            </button>
                            <button type="button" className="primary-button" onClick={handleDownloadTemplate} style={{ backgroundColor: '#6366f1' }}>
                                <FaDownload /> Descargar plantilla
                            </button>
                            <input
                                ref={propietariosUploadRef}
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden-input"
                                onChange={handleExcelUpload}
                            />
                        </div>
                        <p className="help-text">Esta carga solo agrega datos visibles del propietario para contacto rápido en viviendas arrendadas. No modifica cálculos ni reglas del sistema.</p>
                    </article>

                    {/* Sección 'Control operativo' eliminada por requerimiento */}
                </section>
            </div>

            {showSuccessDialog && (
                <ConfirmDialog
                        open={showSuccessDialog}
                    title="✓ Archivo subido correctamente"
                    message={successSummary || "La información del propietario se ha cargado exitosamente. Esta información es solo de vista y no afecta cálculos ni reglas del sistema. Los datos se mostrarán debajo de la información del residente en las tarjetas de viviendas arrendadas."}
                    onConfirm={() => setShowSuccessDialog(false)}
                        onCancel={() => setShowSuccessDialog(false)}
                    tone="success"
                    showCancel={false}
                />
            )}

            {showErrorDialog && (
                <ConfirmDialog
                        open={showErrorDialog}
                    title="Error al subir archivo"
                    message={errorMessage}
                    onConfirm={() => setShowErrorDialog(false)}
                        onCancel={() => setShowErrorDialog(false)}
                    tone="danger"
                    showCancel={false}
                />
            )}

            {uploadMessage && (
                <div className="upload-toast" style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    background: uploadMessage.startsWith('✓') ? '#10b981' : '#ef4444',
                    color: '#fff',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    zIndex: 9999,
                    animation: 'slideIn 0.3s ease-out',
                    fontWeight: '500',
                    maxWidth: '400px',
                }}>
                    {uploadMessage}
                </div>
            )}

            <style>{`
                @keyframes slideIn {
                    from {
                        transform: translateX(400px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default DepartamentosIdentificacion;
